import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { ToastrService } from 'ngx-toastr';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap } from 'rxjs/operators';
import { AccountInfoState } from '../../account-info-header/models/AccountInfoState';
import { selectInfo, selectSubAccountId, selectTemplatesList, selectUsername } from '../../account-info-header/reducers';
import { ModifiedPayeeNames } from '../../modified-first-parties/models/FirstParties';
import { selectModifiedFirstPartyPayeesFormatted } from '../../modified-first-parties/reducers';
import { EAuthInfo } from '../../shared/models/EAuthInfo';
import { selectAcknowledgementDateTime, selectEAuthInfo } from '../../shared/reducers';
import { FeatureFlagService } from '../../shared/services/feature-flag-service';
import { ViewPageActions } from '../../transaction-view/actions';
import { selectViewDetails } from '../../transaction-view/reducers';
import { TransactionsTableActions, WireActions } from '../actions';
import { TransactionForm, WireExtraPrompts, WireForm, WireTransactionDetails } from '../models/form/TransactionForm';
import { Country } from '../models/shared/Country';
import { ServiceError, ServiceErrorWithList } from '../models/shared/ServiceError';
import { ReceivingAccountHolder, WireInfo } from '../models/wire/WireInfo';
import { DraftsCommonModelsWireDraft, WireTemplatesRequest } from '../models/wire/WireTemplatesRequest';
import { DraftCommonWire } from '../models/wire/WireTemplatesResponse';
import { selectCurrentTemplate, selectStagedTransactionForm, selectTransactionForm, selectWireInfo } from '../reducers';
import { DraftsService } from '../services/drafts.service';
import { currencyStringToNumber } from '../utilities/currencyStringToNumber';
import { MailingDetails } from '../models/wire/Intermediary';
import { concatLatestFrom } from '@ngrx/operators';

@Injectable()
export class DraftsEffects {
  constructor(
    private actions$: Actions,
    private draftsService: DraftsService,
    private store: Store,
    private datepipe: DatePipe,
    private toastr: ToastrService,
    private ffs: FeatureFlagService
  ) { }

  callWireTemplatesGetList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WireActions.callWireTemplatesGetList),
      concatLatestFrom(() => [
        this.store.select(selectSubAccountId)
      ]),
      switchMap(([_action, subAccount]) => {
        return this.draftsService.callWireTemplatesGetList(subAccount).pipe(
          map((wireTemplateResponse: DraftCommonWire[]) => {
            return WireActions.callWireTemplatesGetListSuccessful({templates: wireTemplateResponse});
          }),
          catchError((err: ServiceErrorWithList) => of(WireActions.callWireTemplatesGetListFailed(err))
          )
        );
      })
    )
  );

  callWireTemplatesGetListTable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WireActions.callWireTemplatesGetListTable),
      concatLatestFrom(() => [
        this.store.select(selectSubAccountId)
      ]),
      exhaustMap(([_action, subAccount]) => {
        return this.draftsService.callWireTemplatesGetList(subAccount).pipe(
          map((wireTemplateResponse: DraftCommonWire[]) => {
            return WireActions.callWireTemplatesGetListTableSuccessful({templates: wireTemplateResponse});
          }),
          catchError((err: ServiceErrorWithList) => of(WireActions.callWireTemplatesGetListTableFailed(err))
          )
        );
      })
    )
  );

  callWireCreateTemplate$ = createEffect(() =>
  this.actions$.pipe(
      ofType(WireActions.callWiresCreateTemplate),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectStagedTransactionForm),
        this.store.select(selectWireInfo),
        this.store.select(selectEAuthInfo),
        this.store.select(selectAcknowledgementDateTime),
        this.store.select(selectUsername),
        this.store.select(selectModifiedFirstPartyPayeesFormatted)
      ]),
      exhaustMap(([payload, accountInfo, transactionForm, wireInfo, eAuthInfo, acknowledgementConsentDateTime, userName, modifiedPayeeList]) => {
        var request = this.createWireTemplateRequest(
          accountInfo,
          transactionForm,
          wireInfo,
          eAuthInfo,
          acknowledgementConsentDateTime,
          payload.templateNickname,
          userName,
          modifiedPayeeList
        );
        return this.draftsService.callWireCreateTemplate(request).pipe(
          map((response: DraftCommonWire) => {
            return WireActions.callWiresCreateTemplateSuccessful(response);
          }),
          catchError((err: ServiceErrorWithList) =>
            of(WireActions.callWiresCreateTemplateFailed(err))
          )
        );
      })
    ));

    callWireCreateTemplateFromInput$ = createEffect(() =>
      this.actions$.pipe(
          ofType(WireActions.callWireCreateTemplateFromInput),
          concatLatestFrom(() => [
            this.store.select(selectInfo),
            this.store.select(selectTransactionForm),
            this.store.select(selectWireInfo),
            this.store.select(selectEAuthInfo),
            this.store.select(selectAcknowledgementDateTime),
            this.store.select(selectUsername),
            this.store.select(selectModifiedFirstPartyPayeesFormatted)
          ]),
          exhaustMap(([payload, accountInfo, transactionForm, wireInfo, eAuthInfo, acknowledgementConsentDateTime, userName, modifiedPayeeList]) => {
            var request = this.createWireTemplateRequest(
              accountInfo,
              transactionForm,
              wireInfo,
              eAuthInfo,
              acknowledgementConsentDateTime,
              payload.templateNickname,
              userName,
              modifiedPayeeList
            );
            return this.draftsService.callWireCreateTemplate(request).pipe(
              map((response: DraftCommonWire) => {
                return WireActions.callWiresCreateTemplateSuccessful(response);
              }),
              catchError((err: ServiceErrorWithList) =>
                of(WireActions.callWiresCreateTemplateFailed(err))
              )
            );
          })
        ));

  callDeleteWireTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionsTableActions.deleteTemplate),
      concatLatestFrom(() => [
        this.store.select(selectCurrentTemplate),
        this.store.select(selectViewDetails)
      ]),
      switchMap(([_action, currentTemplate, viewDetails]) => {
        return this.draftsService.callDeleteTemplate(currentTemplate).pipe(
          switchMap(() => {
            if (viewDetails.type === 'template') {
              this.store.dispatch(ViewPageActions.backButtonClicked());
            }
            return of(TransactionsTableActions.deleteTemplateSuccess());
          }),
        catchError((err: ServiceError) => {
            this.toastr.error("Failed to delete template")
            return of(TransactionsTableActions.deleteTemplateFailure(err)
            );
        })
        );
      })
    )
  );

  callWirePatchTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionsTableActions.addUpdateDeleteNicknameTemplates),
      concatLatestFrom(() => [
        this.store.select(selectTemplatesList)
      ]),
      switchMap(([{draftId, nickName}, templateList]) => {
        let template: WireTemplatesRequest = templateList.find(template => template.draftId === draftId)!;
        let requestData = {
          ...template,
          draftObject: {
            ...template.draftObject,
            nickname: nickName
          }
        }

        return this.draftsService.callWirePatchTemplate(requestData).pipe(
          map((wireTemplateResponse: DraftCommonWire) => {
            return TransactionsTableActions.updateDraftSuccess(wireTemplateResponse);
          }),
          catchError((err: ServiceErrorWithList) => {
            return of(TransactionsTableActions.updateDraftFailure(err))
          }
          )
        );
      })
    )
  );

  callIsoWirePatchTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WireActions.callWiresPatchTemplate),
      switchMap(({currentTemplate, nickname, form}) => {
        let requestData = this.createIsoWireTemplatePatchRequest(currentTemplate, nickname, form);

        return this.draftsService.callWirePatchTemplate(requestData).pipe(
          map((wireTemplateResponse: DraftCommonWire) => {
            return WireActions.callWiresPatchTemplateSuccessful(wireTemplateResponse);
          }),
          catchError((err: ServiceErrorWithList) => {
            return of(WireActions.callWiresPatchTemplateFailed(err))
          }
          )
        );
      })
    )
  );
  
  createIsoWireTemplatePatchRequest(
    template: WireTemplatesRequest,
    nickname: string,
    wireForm: WireForm
  ): WireTemplatesRequest {
    const transactionDetails = wireForm.transactionDetails;
    const hasIntermediary = transactionDetails.intermediarySelection === 'yes';

    let beneficiaryDetails: MailingDetails;
    let intermediaryDetails: MailingDetails;
    
    beneficiaryDetails = {
      ...template.draftObject.beneficiary,
      name: transactionDetails.name,
      department: transactionDetails.receivingAccountHolderDepartment,
      streetName: transactionDetails.receivingAccountHolderStreetName,
      buildingNumber: transactionDetails.receivingAccountHolderStreetNumber,
      buildingName: transactionDetails.receivingAccountHolderBuildingName,
      floor: transactionDetails.receivingAccountHolderFloor,
      room: transactionDetails.receivingAccountHolderApartmentSuiteEtc,
      zipCode: transactionDetails.receivingAccountHolderZip,
      townName: transactionDetails.receivingAccountHolderCity,
      townLocationName: transactionDetails.receivingAccountHolderCountry !== 'US' ? transactionDetails.receivingAccountHolderTownLocationName : '',
      districtName: transactionDetails.receivingAccountHolderCountry !== 'US' ? transactionDetails.receivingAccountHolderDistrict : '',
      countrySubDivision: transactionDetails.receivingAccountHolderState,
      country: transactionDetails.receivingAccountHolderCountry
    }

    let request = {
      ...template,
      draftObject: {
        ...template.draftObject,
        beneficiary: beneficiaryDetails,
        beneficiaryBank: {
          ...template.draftObject.beneficiaryBank,
          name: transactionDetails.receivingBankName,
          townName: transactionDetails.receivingBankCity,
          countrySubDivision: transactionDetails.receivingBankState
        },
        intermediary: {
          ...template.draftObject.intermediary
        },
        nickname: nickname ? nickname : template.draftObject.nickname
      }
    }

    if (hasIntermediary) {
      intermediaryDetails = {
        ...template.draftObject.intermediary.mailingDetails,
        name: transactionDetails.intermediaryName,
        department: transactionDetails.intermediaryDepartment,
        streetName: transactionDetails.intermediaryStreetName, 
        buildingNumber: transactionDetails.intermediaryStreetNumber, 
        buildingName: transactionDetails.intermediaryBuildingName, 
        floor: transactionDetails.intermediaryFloor,
        room: transactionDetails.intermediaryApartmentSuiteEtc,
        zipCode: transactionDetails.intermediaryZip,
        townName: transactionDetails.intermediaryCity,
        townLocationName: transactionDetails.intermediaryCountry !== 'US' ? transactionDetails.intermediaryTownLocationName : '',
        districtName: transactionDetails.intermediaryCountry !== 'US' ? transactionDetails.intermediaryDistrict : '',
        countrySubDivision: transactionDetails.intermediaryState,
        country: transactionDetails.intermediaryCountry
      }
      request.draftObject.intermediary.mailingDetails = intermediaryDetails;
    }

    return request;
  }

  createWireTemplateRequest(
    accountInfo: AccountInfoState,
    form: TransactionForm,
    wireInfo: WireInfo,
    eAuthInfo: EAuthInfo,
    acknowledgementConsentDateTime: string,
    nickname: string,
    userName: string,
    modifiedPayeeList: ModifiedPayeeNames[],
  ): WireTemplatesRequest {
    let transactionDetails = form.transactionDetails as WireTransactionDetails;
    let extraPrompts = form.extraPrompts as WireExtraPrompts;
    let draft = new DraftsCommonModelsWireDraft({
      masterAccountId: accountInfo.masterAccount,
      subAccountId: accountInfo.accountId,
      isUseSloa: extraPrompts?.useStandingAuth === 'yes' ? true : false,
      initiatedBy: userName,
      isSubmitAndCreateSloa: wireInfo.isSubmitAndCreate ?? false,
      originalRequestedDate: wireInfo.requestedDate,
      acknowledgementConsentDateTime: acknowledgementConsentDateTime,
      frequency: extraPrompts?.useStandingAuth === 'yes' ? 'ONREQST' : 'ONETIME',
      amount: currencyStringToNumber(transactionDetails.amount),
      isThirdParty: transactionDetails.recipient === 'thirdParty',
      isEscrow: transactionDetails.isEscrow,
      beneficiaryBankId: wireInfo.isFedWire ? transactionDetails.aba : transactionDetails.bankID,
      beneficiaryAccount: transactionDetails.toAccount?.toUpperCase(),
      beneficiary: {
        namePartOne: transactionDetails.receivingAccountHolderName1?.toUpperCase(),
        namePartTwo: transactionDetails.receivingAccountHolderName2?.toUpperCase(),
        addressLineOne: transactionDetails.receivingAccountHolderAddress1?.toUpperCase(),
        addressLineTwo: transactionDetails.receivingAccountHolderAddress2?.toUpperCase(),
        phoneNumber: '',
        name: transactionDetails.name,
        countryCode: transactionDetails.receivingAccountHolderCountry,
        countryName: this.getCountryDescriptionOrCode(wireInfo.countryList, transactionDetails.receivingAccountHolderCountry, true),
        department: transactionDetails.receivingAccountHolderDepartment,
        streetName: transactionDetails.receivingAccountHolderStreetName,
        buildingNumber: transactionDetails.receivingAccountHolderStreetNumber,
        buildingName: transactionDetails.receivingAccountHolderBuildingName,
        floor: transactionDetails.receivingAccountHolderFloor,
        room: transactionDetails.receivingAccountHolderApartmentSuiteEtc,
        zipCode: transactionDetails.receivingAccountHolderZip,
        townName: transactionDetails.receivingAccountHolderCity,
        townLocationName: transactionDetails.receivingAccountHolderCountry !== 'US' ? transactionDetails.receivingAccountHolderTownLocationName : '',
        districtName: transactionDetails.receivingAccountHolderCountry !== 'US' ? transactionDetails.receivingAccountHolderDistrict : '',
        countrySubDivision: transactionDetails.receivingAccountHolderState,
        country: transactionDetails.receivingAccountHolderCountry
      },
      beneficiaryBank: {
        name: transactionDetails.receivingBankName,
        namePartOne: transactionDetails.receivingBankName1,
        namePartTwo: transactionDetails.receivingBankName2,
        addressLineOne: transactionDetails.receivingBankAddress1,
        addressLineTwo: transactionDetails.receivingBankAddress2,
        phoneNumber: transactionDetails.bankPhoneNumber ?? '',
        countryCode: this.getCountryDescriptionOrCode(wireInfo.countryList, transactionDetails.receivingBankCountry, false),
        countryName: transactionDetails.receivingBankCountry,
        townName: transactionDetails.receivingBankCity,
        countrySubDivision: transactionDetails.receivingBankState
      },
      note: transactionDetails.transmissionNotes,
      nickname: nickname,
      creditTo: {
        id: wireInfo.creditTo.insitutionAccountNumber ?? '',
        mailingDetails: {
          namePartOne: wireInfo.creditTo.insitutionName1 ?? '',
          namePartTwo: wireInfo.creditTo.insitutionName2 ?? '',
          addressLineOne: wireInfo.creditTo.institutionAddress ?? '',
          addressLineTwo: wireInfo.creditTo.institutionAddressContd ?? '',
          phoneNumber: wireInfo.creditTo.institutionPhoneNumber ?? '',
          countryCode: wireInfo.creditTo.institutionCountry ?? '',
          countryName: ''
        }
      },
      isProcessDateExtended: wireInfo.processDateFields.isExtendedProcessingWindow,
      isUsingEauth: eAuthInfo.isAccountHolderSelected,
      isFirstPartyModified: this.isFirstPartyModified(wireInfo.receivingAccountHolder, transactionDetails, modifiedPayeeList),
      isIrsAba: wireInfo.isIrsAba,
      taxRemittance: wireInfo?.isIrsAba ? {
        taxId: transactionDetails.taxPayerId ?? null,
        taxMonthCode: transactionDetails.taxMonth ?? null,
        taxTypeCode: transactionDetails.taxTypeCode ?? null,
        taxYear: transactionDetails.taxYear ?? null,
        additionalInfo: transactionDetails.transmissionNotes ?? null
      } : undefined
    })

    if (eAuthInfo.isAccountHolderSelected) {
      const selectedCustomer = wireInfo.eAuthModel.custAcctAuthorizationsList.eAuthCustomerList.find(customer => customer.customerId === eAuthInfo.selectedAccountHolder[0]);
      if (selectedCustomer) {
        draft.transactionEAuthClient.customerId = selectedCustomer.customerId;
        draft.transactionEAuthClient.emailId = selectedCustomer.emailId;
        draft.transactionEAuthClient.firstName = selectedCustomer.firstName;
        draft.transactionEAuthClient.lastName = selectedCustomer.lastName;
        draft.transactionEAuthClient.loginIdEnrollStatus = selectedCustomer.loginIdEnrollStatus;
        draft.transactionEAuthClient.organizationName = selectedCustomer.organizationName;
        draft.transactionEAuthClient.typeCode = selectedCustomer.typeCode;
        draft.transactionEAuthClient.customerRestrictionReason = selectedCustomer.customerRestrictionReason;
        draft.transactionEAuthClient.accountRoles = selectedCustomer.accountRoles;
        draft.transactionEAuthClient.isCustomerWebEnrolled = selectedCustomer.isCustomerWebEnrolled;
        draft.transactionEAuthClient.isAccountWebEnabled = selectedCustomer.isAccountWebEnabled,
        draft.transactionEAuthClient.isDisplayAllowed = selectedCustomer.isDisplayAllowed,
        draft.transactionEAuthClient.eauthInd = selectedCustomer.eauthInd,
        draft.transactionEAuthClient.isCustomerEAuthEligible = selectedCustomer.isCustomerEAuthEligible
      }
    }

    if (transactionDetails.intermediarySelection === 'yes') {
      draft.intermediary = {
        mailingDetails: {
          namePartOne: transactionDetails.intermediaryName?.toUpperCase(),
          namePartTwo: transactionDetails.intermediaryNameCont?.toUpperCase(),
          addressLineOne: transactionDetails.intermediaryAddress?.toUpperCase(),
          addressLineTwo: transactionDetails.intermediaryAddressCont?.toUpperCase(),
          phoneNumber: transactionDetails.intermediaryPhone,
          countryCode: this.getCountryDescriptionOrCode(wireInfo.countryList, transactionDetails.intermediaryCountry.toUpperCase(), false),
          countryName: transactionDetails.intermediaryCountry?.toUpperCase(),
          name: transactionDetails.intermediaryName,
          department: transactionDetails.intermediaryDepartment,
          streetName: transactionDetails.intermediaryStreetName, 
          buildingNumber: transactionDetails.intermediaryStreetNumber, 
          buildingName: transactionDetails.intermediaryBuildingName, 
          floor: transactionDetails.intermediaryFloor,
          room: transactionDetails.intermediaryApartmentSuiteEtc,
          zipCode: transactionDetails.intermediaryZip,
          townName: transactionDetails.intermediaryCity,
          townLocationName: transactionDetails.intermediaryCountry !== 'US' ? transactionDetails.intermediaryTownLocationName : '',
          districtName: transactionDetails.intermediaryCountry !== 'US' ? transactionDetails.intermediaryDistrict : '',
          countrySubDivision: transactionDetails.intermediaryState,
          country: transactionDetails.intermediaryCountry
        },
        id: transactionDetails.intermediaryAccountNumber?.toUpperCase(),
      }
    }

    if (accountInfo.classifications.isIra) {
      const fedTaxPercent = +(transactionDetails.taxWithholdingsFormGroup?.fedTaxPercent?.replace('%', '') ?? '0');
      const stateTaxPercent = +(transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent?.replace('%', '') ?? '0');

      draft.iraTransactionRequest = {
        distributionReason: wireInfo.distributionReasonMap.find(elem => elem.description === transactionDetails.taxWithholdingsFormGroup?.distributionReason)!,
        stateTaxPercentage: +(transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent?.replace('%', '') ?? '0'),
        fedTaxPercentage: +(transactionDetails.taxWithholdingsFormGroup?.fedTaxPercent?.replace('%', '') ?? '0'),
        isWithholdFedTax: fedTaxPercent !== 0,
        isWithholdStateTax: stateTaxPercent !== 0,
        isGrossAmt: transactionDetails.amountType === 'gross',
      }
    };

    return new WireTemplatesRequest({
      subAccount: accountInfo.accountId,
      masterAccount: accountInfo.masterAccount,
      transactionAmount: currencyStringToNumber(transactionDetails.amount),
      transactionProcessDate: this.datepipe.transform(
        transactionDetails.selectedDate,
        'yyyy-MM-dd'
      ) ?? '',
      createDate: DateTime.now().toFormat('yyyy-MM-dd'),
      createdByLoginId: userName,
      draftObject: draft,
    })
  }

  getCountryDescriptionOrCode(countryList: Country[], countryInfo: string, isCountryCode: boolean): string {
    if (isCountryCode){
      return (countryInfo === 'US') ? 'UNITED STATES' : countryList.find(c => c.code === countryInfo)?.description ?? countryInfo
    } else {
      return (countryInfo === 'UNITED STATES') ? 'US' : countryList.find(c => c.description === countryInfo)?.code ?? countryInfo
    }
  }

  isFirstPartyModified(
    receivingAccountHolder: ReceivingAccountHolder,
    { recipient, receivingAccountHolderName1, receivingAccountHolderName2 }: { recipient: string, receivingAccountHolderName1: string, receivingAccountHolderName2: string },
    modifiedPayeeList: ModifiedPayeeNames[]): boolean {
    if(recipient !== 'firstParty') return false;

      if (receivingAccountHolder.accountHolderName1 === receivingAccountHolderName1 && receivingAccountHolder.accountHolderName2 === receivingAccountHolderName2){
        return false;
      } else {
        const payee = modifiedPayeeList.find(payee => payee.name1 === receivingAccountHolderName1 && payee.name2 === receivingAccountHolderName2);
        if (payee){
          return !payee.isPrimary;
        }
        return true;
      }
    }
  }
